/**
 * CR098: Card Styling Panel
 * 
 * Controls visual styling of deal cards:
 * - Corner radius (presets + slider)
 * - Background (solid/gradient with colors)
 * - Border (color + width)
 * - Shadow intensity
 * - Header styling (height, background, decorations)
 * - Decoration positioning (ribbon, starburst)
 */

import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { 
  Paintbrush, 
  Circle, 
  Square, 
  Layers, 
  Sun, 
  Move, 
  ChevronDown,
  Ribbon,
  Star,
  Image as ImageIcon,
} from "lucide-react";
import { useState } from "react";

// ================================================================
// TYPES
// ================================================================

export interface CardStyling {
  // Card Container
  cornerRadius: number; // px
  cornerRadiusPreset: "sm" | "md" | "lg" | "xl" | "custom";
  
  // Background
  backgroundType: "solid" | "gradient";
  backgroundColor: string; // HSL value
  gradientFrom: string;
  gradientTo: string;
  gradientDirection: "to-r" | "to-br" | "to-b" | "to-bl";
  
  // Border
  borderWidth: number;
  borderColor: string;
  
  // Shadow
  shadowIntensity: "none" | "low" | "medium" | "high";
  
  // Header
  headerHeight: "sm" | "md" | "lg";
  headerBackgroundType: "solid" | "gradient";
  headerBackgroundColor: string;
  headerGradientFrom: string;
  headerGradientTo: string;
  showMerchantLogo: boolean;
  showMerchantName: boolean;
  
  // Decorations
  showRibbon: boolean;
  ribbonPosition: "top-right" | "top-left";
  ribbonOffsetX: number;
  ribbonOffsetY: number;
  
  showStarburst: boolean;
  starburstPosition: "top-left" | "top-right";
  starburstOffsetX: number;
  starburstOffsetY: number;
  
  // CRRIBBON: Ribbon styling (HEX-based colors)
  ribbonBaseHex?: string | null;
  ribbonBorderHex?: string | null;
  ribbonGradientFrom?: string; // Derived HSL
  ribbonGradientTo?: string;   // Derived HSL
  ribbonBorderColor?: string;  // Derived HSLA
}

export const DEFAULT_CARD_STYLING: CardStyling = {
  cornerRadius: 24,
  cornerRadiusPreset: "lg",
  backgroundType: "solid",
  backgroundColor: "0 0% 100%", // white
  gradientFrom: "0 0% 100%",
  gradientTo: "0 0% 96%",
  gradientDirection: "to-b",
  borderWidth: 1,
  borderColor: "0 0% 0% / 0.05",
  shadowIntensity: "medium",
  headerHeight: "md",
  headerBackgroundType: "gradient",
  headerBackgroundColor: "0 0% 10%",
  headerGradientFrom: "0 0% 10% / 0.9",
  headerGradientTo: "0 0% 20% / 0.7",
  showMerchantLogo: true,
  showMerchantName: true,
  showRibbon: true,
  ribbonPosition: "top-right",
  ribbonOffsetX: 0,
  ribbonOffsetY: 0,
  showStarburst: true,
  starburstPosition: "top-left",
  starburstOffsetX: -28,
  starburstOffsetY: -20,
};

// Theme Presets
export const THEME_PRESETS: Record<string, Partial<CardStyling>> = {
  light: {
    cornerRadius: 24,
    cornerRadiusPreset: "lg",
    backgroundType: "solid",
    backgroundColor: "0 0% 100%",
    borderWidth: 1,
    borderColor: "0 0% 0% / 0.05",
    shadowIntensity: "medium",
    headerBackgroundType: "gradient",
    headerBackgroundColor: "0 0% 10%",
    headerGradientFrom: "0 0% 10% / 0.9",
    headerGradientTo: "0 0% 20% / 0.7",
  },
  dark: {
    cornerRadius: 28,
    cornerRadiusPreset: "xl",
    backgroundType: "gradient",
    backgroundColor: "152 28% 20%",
    gradientFrom: "152 28% 20%",
    gradientTo: "152 28% 28%",
    gradientDirection: "to-b",
    borderWidth: 1,
    borderColor: "152 28% 40% / 0.3",
    shadowIntensity: "low",
    headerBackgroundType: "solid",
    headerBackgroundColor: "152 28% 15%",
    headerGradientFrom: "152 28% 15%",
    headerGradientTo: "152 28% 20%",
  },
};

const RADIUS_PRESETS = {
  sm: 8,
  md: 16,
  lg: 24,
  xl: 28,
};

const SHADOW_CLASSES = {
  none: "shadow-none",
  low: "shadow-sm",
  medium: "shadow-[0_10px_30px_rgba(0,0,0,0.08)]",
  high: "shadow-[0_20px_50px_rgba(0,0,0,0.15)]",
};

const HEADER_HEIGHTS = {
  sm: "h-16",
  md: "h-20 md:h-24",
  lg: "h-24 md:h-28",
};

// ================================================================
// COMPONENT
// ================================================================

interface CardStylingPanelProps {
  styling: CardStyling;
  onChange: (update: Partial<CardStyling>) => void;
  themePreset: "light" | "dark" | "custom";
  onThemeChange: (theme: "light" | "dark" | "custom") => void;
}

export function CardStylingPanel({
  styling,
  onChange,
  themePreset,
  onThemeChange,
}: CardStylingPanelProps) {
  const [openSections, setOpenSections] = useState({
    container: true,
    background: false,
    header: false,
    decorations: false,
  });

  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleRadiusPresetChange = (preset: string) => {
    if (preset === "custom") {
      onChange({ cornerRadiusPreset: "custom" });
    } else {
      const radiusValue = RADIUS_PRESETS[preset as keyof typeof RADIUS_PRESETS];
      onChange({ 
        cornerRadiusPreset: preset as CardStyling["cornerRadiusPreset"],
        cornerRadius: radiusValue,
      });
    }
    onThemeChange("custom");
  };

  const handleThemePresetChange = (theme: "light" | "dark" | "custom") => {
    onThemeChange(theme);
    if (theme !== "custom") {
      const preset = THEME_PRESETS[theme];
      if (preset) {
        onChange(preset);
      }
    }
  };

  return (
    <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Paintbrush className="h-4 w-4 text-muted-foreground" />
          <Label className="font-medium">Card Styling</Label>
        </div>
        <Badge variant="outline" className="text-xs">
          Stored in Template
        </Badge>
      </div>

      {/* Theme Preset Selector */}
      <div className="space-y-2">
        <Label className="text-sm">Theme Preset</Label>
        <Select value={themePreset} onValueChange={(v) => handleThemePresetChange(v as "light" | "dark" | "custom")}>
          <SelectTrigger className="h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="light">
              <div className="flex items-center gap-2">
                <Sun className="h-4 w-4" />
                Light
              </div>
            </SelectItem>
            <SelectItem value="dark">
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 rounded bg-slate-800" />
                Dark
              </div>
            </SelectItem>
            <SelectItem value="custom">
              <div className="flex items-center gap-2">
                <Paintbrush className="h-4 w-4" />
                Custom
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Container Section */}
      <Collapsible open={openSections.container} onOpenChange={() => toggleSection("container")}>
        <CollapsibleTrigger className="flex items-center justify-between w-full py-2 hover:bg-muted/50 rounded px-2">
          <div className="flex items-center gap-2">
            <Square className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Container</span>
          </div>
          <ChevronDown className={`h-4 w-4 transition-transform ${openSections.container ? "rotate-180" : ""}`} />
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-4 pt-2 pl-6">
          {/* Corner Radius */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Corner Radius</Label>
              <span className="text-xs text-muted-foreground">{styling.cornerRadius}px</span>
            </div>
            <div className="flex gap-2">
              <Select 
                value={styling.cornerRadiusPreset} 
                onValueChange={handleRadiusPresetChange}
              >
                <SelectTrigger className="w-24 h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sm">Small (8px)</SelectItem>
                  <SelectItem value="md">Medium (16px)</SelectItem>
                  <SelectItem value="lg">Large (24px)</SelectItem>
                  <SelectItem value="xl">Extra (28px)</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
              <Slider
                value={[styling.cornerRadius]}
                onValueChange={([v]) => {
                  onChange({ cornerRadius: v, cornerRadiusPreset: "custom" });
                  onThemeChange("custom");
                }}
                min={0}
                max={48}
                step={2}
                className="flex-1"
              />
            </div>
          </div>

          {/* Border */}
          <div className="space-y-2">
            <Label className="text-xs">Border Width</Label>
            <Slider
              value={[styling.borderWidth]}
              onValueChange={([v]) => {
                onChange({ borderWidth: v });
                onThemeChange("custom");
              }}
              min={0}
              max={4}
              step={1}
              className="w-full"
            />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>0px</span>
              <span>{styling.borderWidth}px</span>
              <span>4px</span>
            </div>
          </div>

          {/* Shadow */}
          <div className="space-y-2">
            <Label className="text-xs">Shadow Intensity</Label>
            <Select 
              value={styling.shadowIntensity} 
              onValueChange={(v) => {
                onChange({ shadowIntensity: v as CardStyling["shadowIntensity"] });
                onThemeChange("custom");
              }}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Background Section */}
      <Collapsible open={openSections.background} onOpenChange={() => toggleSection("background")}>
        <CollapsibleTrigger className="flex items-center justify-between w-full py-2 hover:bg-muted/50 rounded px-2">
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Background</span>
          </div>
          <ChevronDown className={`h-4 w-4 transition-transform ${openSections.background ? "rotate-180" : ""}`} />
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-4 pt-2 pl-6">
          {/* Background Type */}
          <div className="space-y-2">
            <Label className="text-xs">Type</Label>
            <Select 
              value={styling.backgroundType} 
              onValueChange={(v) => {
                onChange({ backgroundType: v as "solid" | "gradient" });
                onThemeChange("custom");
              }}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="solid">Solid Color</SelectItem>
                <SelectItem value="gradient">Gradient</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {styling.backgroundType === "solid" ? (
            <div className="space-y-2">
              <Label className="text-xs">Background Color (HSL)</Label>
              <Input
                value={styling.backgroundColor}
                onChange={(e) => {
                  onChange({ backgroundColor: e.target.value });
                  onThemeChange("custom");
                }}
                placeholder="0 0% 100%"
                className="h-8 text-xs font-mono"
              />
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label className="text-xs">Gradient From (HSL)</Label>
                <Input
                  value={styling.gradientFrom}
                  onChange={(e) => {
                    onChange({ gradientFrom: e.target.value });
                    onThemeChange("custom");
                  }}
                  placeholder="0 0% 100%"
                  className="h-8 text-xs font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Gradient To (HSL)</Label>
                <Input
                  value={styling.gradientTo}
                  onChange={(e) => {
                    onChange({ gradientTo: e.target.value });
                    onThemeChange("custom");
                  }}
                  placeholder="0 0% 96%"
                  className="h-8 text-xs font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Direction</Label>
                <Select 
                  value={styling.gradientDirection} 
                  onValueChange={(v) => {
                    onChange({ gradientDirection: v as CardStyling["gradientDirection"] });
                    onThemeChange("custom");
                  }}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="to-r">Left to Right</SelectItem>
                    <SelectItem value="to-br">Top-Left to Bottom-Right</SelectItem>
                    <SelectItem value="to-b">Top to Bottom</SelectItem>
                    <SelectItem value="to-bl">Top-Right to Bottom-Left</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </CollapsibleContent>
      </Collapsible>

      {/* Header Section */}
      <Collapsible open={openSections.header} onOpenChange={() => toggleSection("header")}>
        <CollapsibleTrigger className="flex items-center justify-between w-full py-2 hover:bg-muted/50 rounded px-2">
          <div className="flex items-center gap-2">
            <ImageIcon className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Header</span>
          </div>
          <ChevronDown className={`h-4 w-4 transition-transform ${openSections.header ? "rotate-180" : ""}`} />
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-4 pt-2 pl-6">
          {/* Header Height */}
          <div className="space-y-2">
            <Label className="text-xs">Height</Label>
            <Select 
              value={styling.headerHeight} 
              onValueChange={(v) => {
                onChange({ headerHeight: v as "sm" | "md" | "lg" });
                onThemeChange("custom");
              }}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sm">Small</SelectItem>
                <SelectItem value="md">Medium</SelectItem>
                <SelectItem value="lg">Large</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Show Merchant Logo - CR225: AUTHORITATIVE source */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-xs">Show Merchant Logo</Label>
              <p className="text-[10px] text-muted-foreground">Controls logo visibility in header</p>
            </div>
            <Switch
              checked={styling.showMerchantLogo}
              onCheckedChange={(v) => {
                onChange({ showMerchantLogo: v });
                onThemeChange("custom");
              }}
            />
          </div>

          {/* Show Merchant Name - CR225: AUTHORITATIVE source (syncs to sections.merchant_name) */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-xs">Show Merchant Name</Label>
              <p className="text-[10px] text-muted-foreground">Master toggle: hides name everywhere</p>
            </div>
            <Switch
              checked={styling.showMerchantName}
              onCheckedChange={(v) => {
                onChange({ showMerchantName: v });
                onThemeChange("custom");
              }}
            />
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Decorations Section */}
      <Collapsible open={openSections.decorations} onOpenChange={() => toggleSection("decorations")}>
        <CollapsibleTrigger className="flex items-center justify-between w-full py-2 hover:bg-muted/50 rounded px-2">
          <div className="flex items-center gap-2">
            <Move className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Decorations</span>
          </div>
          <ChevronDown className={`h-4 w-4 transition-transform ${openSections.decorations ? "rotate-180" : ""}`} />
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-4 pt-2 pl-6">
          {/* Ribbon - CR225: AUTHORITATIVE source for ribbon visibility */}
          <div className="space-y-3 p-3 bg-background rounded border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Ribbon className="h-4 w-4 text-muted-foreground" />
                <div className="space-y-0.5">
                  <Label className="text-xs font-medium">Show Ribbon</Label>
                  <p className="text-[10px] text-muted-foreground">Master toggle: hides deal type ribbon</p>
                </div>
              </div>
              <Switch
                checked={styling.showRibbon}
                onCheckedChange={(v) => {
                  onChange({ showRibbon: v });
                  onThemeChange("custom");
                }}
              />
            </div>
            {styling.showRibbon && (
              <>
                <div className="space-y-2">
                  <Label className="text-xs">Position</Label>
                  <Select 
                    value={styling.ribbonPosition} 
                    onValueChange={(v) => {
                      onChange({ ribbonPosition: v as "top-right" | "top-left" });
                      onThemeChange("custom");
                    }}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="top-right">Top Right</SelectItem>
                      <SelectItem value="top-left">Top Left</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">X Offset</Label>
                    <Input
                      type="number"
                      value={styling.ribbonOffsetX}
                      onChange={(e) => {
                        onChange({ ribbonOffsetX: parseInt(e.target.value) || 0 });
                        onThemeChange("custom");
                      }}
                      className="h-7 text-xs"
                      min={-24}
                      max={24}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Y Offset</Label>
                    <Input
                      type="number"
                      value={styling.ribbonOffsetY}
                      onChange={(e) => {
                        onChange({ ribbonOffsetY: parseInt(e.target.value) || 0 });
                        onThemeChange("custom");
                      }}
                      className="h-7 text-xs"
                      min={-24}
                      max={24}
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Starburst - CR225: AUTHORITATIVE source for starburst visibility */}
          <div className="space-y-3 p-3 bg-background rounded border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Star className="h-4 w-4 text-muted-foreground" />
                <div className="space-y-0.5">
                  <Label className="text-xs font-medium">Show Starburst</Label>
                  <p className="text-[10px] text-muted-foreground">Value badge in corner</p>
                </div>
              </div>
              <Switch
                checked={styling.showStarburst}
                onCheckedChange={(v) => {
                  onChange({ showStarburst: v });
                  onThemeChange("custom");
                }}
              />
            </div>
            {styling.showStarburst && (
              <>
                <div className="space-y-2">
                  <Label className="text-xs">Position</Label>
                  <Select 
                    value={styling.starburstPosition} 
                    onValueChange={(v) => {
                      onChange({ starburstPosition: v as "top-left" | "top-right" });
                      onThemeChange("custom");
                    }}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="top-left">Top Left</SelectItem>
                      <SelectItem value="top-right">Top Right</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">X Offset</Label>
                    <Input
                      type="number"
                      value={styling.starburstOffsetX}
                      onChange={(e) => {
                        onChange({ starburstOffsetX: parseInt(e.target.value) || 0 });
                        onThemeChange("custom");
                      }}
                      className="h-7 text-xs"
                      min={-50}
                      max={50}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Y Offset</Label>
                    <Input
                      type="number"
                      value={styling.starburstOffsetY}
                      onChange={(e) => {
                        onChange({ starburstOffsetY: parseInt(e.target.value) || 0 });
                        onThemeChange("custom");
                      }}
                      className="h-7 text-xs"
                      min={-50}
                      max={50}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Preview Swatch */}
      <div className="pt-3 border-t">
        <Label className="text-xs text-muted-foreground mb-2 block">Style Preview</Label>
        <div 
          className="h-16 w-full transition-all"
          style={{
            borderRadius: `${styling.cornerRadius}px`,
            background: styling.backgroundType === "solid" 
              ? `hsl(${styling.backgroundColor})`
              : `linear-gradient(${styling.gradientDirection === "to-r" ? "90deg" : styling.gradientDirection === "to-br" ? "135deg" : styling.gradientDirection === "to-b" ? "180deg" : "225deg"}, hsl(${styling.gradientFrom}), hsl(${styling.gradientTo}))`,
            border: `${styling.borderWidth}px solid hsl(${styling.borderColor})`,
            boxShadow: styling.shadowIntensity === "none" ? "none" 
              : styling.shadowIntensity === "low" ? "0 2px 8px rgba(0,0,0,0.05)"
              : styling.shadowIntensity === "medium" ? "0 10px 30px rgba(0,0,0,0.08)"
              : "0 20px 50px rgba(0,0,0,0.15)",
          }}
        />
      </div>
    </div>
  );
}

// Export helpers for DealGiftCard to consume
export function getShadowClass(intensity: CardStyling["shadowIntensity"]): string {
  return SHADOW_CLASSES[intensity] || SHADOW_CLASSES.medium;
}

export function getHeaderHeightClass(height: CardStyling["headerHeight"]): string {
  return HEADER_HEIGHTS[height] || HEADER_HEIGHTS.md;
}
